// The fan dial WITH its input — the 270° gauge you drag (or tap) around to set
// a speed. One copy for both shells (2026-10-03): the phone's FanScreen and the
// desktop fan card both render this, so the desktop shows the same dial, steps
// and command the phone does instead of its own four-segment wheel.
//
// Lifted out of FanScreen unchanged:
//   • state is the DEVICE's — seeded from the server, then followed on
//     `<uno>/client`; `known` stays false until something real arrives
//   • a dead hub, so a tap on the blades does nothing
//   • drag shows a preview, and ONE command is published when the pointer lifts
//     (never one per pointermove)
// FanGauge (drawing only) is untouched; this is the part that was screen-local.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import FanGauge, { DIAL_START, DIAL_SWEEP } from './FanGauge';
import { useApp } from '../../../core/state/AppContext';
import { publishCommand } from '../../../core/utils/deviceController';
import { subscribeTopic, unsubscribeTopic, addMessageListener } from '../../../core/mqtt/mqttClient';
import { fetchSeedStatus } from '../../../core/devices/deviceStatus';
import {
  FAN_SPEEDS, fanCommand, fanFraction, fanStatusLabel, fanStepLabel, parseFanSpeed,
} from '../../../core/devices/fan';

export default function FanDialControl({ device, size }) {
  const { userId } = useApp();
  const uno = device?.UniqueNumber;

  const [speed, setSpeed] = useState(FAN_SPEEDS[0]);
  const [known, setKnown] = useState(false);
  const [dragSpeed, setDragSpeed] = useState(null);
  const boxRef = useRef(null);
  const displaySpeed = dragSpeed ?? speed;

  // Seed from the last status the server recorded.
  useEffect(() => {
    if (!uno) return undefined;
    let alive = true;
    fetchSeedStatus(uno).then((raw) => {
      const s = parseFanSpeed(raw);
      if (alive && s != null) { setSpeed(s); setKnown(true); }
    });
    return () => { alive = false; };
  }, [uno]);

  // Then follow the device.
  useEffect(() => {
    if (!uno) return undefined;
    const topic = `${uno}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      const open = payload.indexOf('[');
      const close = payload.indexOf(']');
      if (open === -1 || close === -1) return;
      if (payload.substring(open + 1, close) !== String(uno)) return;
      const s = parseFanSpeed(payload);
      if (s != null) { setSpeed(s); setKnown(true); }
    });
    return () => { off(); unsubscribeTopic(topic); };
  }, [uno]);

  const handleTouch = useCallback((clientX, clientY) => {
    const el = boxRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = clientX - rect.left - size / 2;
    const dy = clientY - rect.top - size / 2;
    if (Math.hypot(dx, dy) < size * 0.22) return; // dead hub

    let t = (Math.atan2(dy, dx) - DIAL_START) % (2 * Math.PI);
    if (t < 0) t += 2 * Math.PI;
    if (t > DIAL_SWEEP) {
      t = (t - DIAL_SWEEP) < (2 * Math.PI - t) ? DIAL_SWEEP : 0;
    }
    const idx = Math.round((t / DIAL_SWEEP) * (FAN_SPEEDS.length - 1));
    setDragSpeed(FAN_SPEEDS[Math.min(FAN_SPEEDS.length - 1, Math.max(0, idx))]);
  }, [size]);

  const commit = useCallback(() => {
    if (dragSpeed == null) return;
    setSpeed(dragSpeed);
    setKnown(true);
    publishCommand(uno, fanCommand(dragSpeed), userId);
    setDragSpeed(null);
  }, [dragSpeed, uno, userId]);

  return (
    <Box
      ref={boxRef}
      sx={{ width: size, height: size, position: 'relative', touchAction: 'none', cursor: 'pointer' }}
      onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); handleTouch(e.clientX, e.clientY); }}
      onPointerMove={(e) => { if (e.buttons) handleTouch(e.clientX, e.clientY); }}
      onPointerUp={commit}
      onPointerCancel={commit}
    >
      <FanGauge
        size={size}
        frac={fanFraction(displaySpeed)}
        running={known && displaySpeed > 10}
        known={known}
        stepLabel={fanStepLabel(displaySpeed)}
        statusLabel={fanStatusLabel(displaySpeed)}
      />
    </Box>
  );
}
